import { useCallback, useEffect, useRef, useState } from "react";
import {
  agenteSisodontoService,
  caixaService,
} from "../../services/apiService";
import {
  AcompanhamentoCancelado,
  ETAPAS,
  MENSAGEM_ROBO_OFFLINE,
  PRAZO_CRIACAO_PEDIDO_MS,
  PrazoEsgotado,
  aguardarPedidoFinal,
  comPrazo,
  diasDoPeriodo,
  mensagemDeFalhaDoPedido,
} from "./integracaoSisodontoUtils";

export const INTERVALO_STATUS_ROBO_MS = 15000;

/**
 * Integração com o Sisodonto pelo robô local, dia a dia.
 *
 * O período escolhido vira uma sequência de pedidos de um dia cada: o robô
 * termina um dia antes de o próximo ser pedido. Assim o progresso é por dia,
 * os dias concluídos ficam salvos mesmo se um dia falhar, e a execução para
 * no primeiro erro (por exemplo, sessão do Sisodonto expirada).
 *
 * `execucao` é null com o modal em "escolher período"; depois traz:
 * - dias: [{ data, status, etapa, mensagem }] com status aguardando,
 *   executando, criado, ignorado, erro ou nao_executado;
 * - estado: "executando" ou "finalizado";
 * - motivoFim: "sucesso", "interrompido" ou "erro" (só quando finalizado);
 * - mensagemErro e sessaoExpirada (quando o fim foi um erro).
 */
const useIntegracaoSisodonto = ({ onCaixasCriados } = {}) => {
  const [agente, setAgente] = useState({ carregado: false, online: false });
  const [execucao, setExecucao] = useState(null);

  const retornosRef = useRef({});
  retornosRef.current = { onCaixasCriados };

  const montadoRef = useRef(true);
  const executandoRef = useRef(false);
  const interrompidoRef = useRef(false);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  // Estado do robô (online/offline).
  useEffect(() => {
    let encerrado = false;

    const consultarStatus = async () => {
      try {
        const status = await agenteSisodontoService.status();
        if (!encerrado) {
          setAgente({ carregado: true, ...status });
        }
      } catch (erro) {
        if (!encerrado) {
          setAgente({ carregado: true, online: false });
        }
      }
    };

    consultarStatus();
    const intervalo = setInterval(consultarStatus, INTERVALO_STATUS_ROBO_MS);
    return () => {
      encerrado = true;
      clearInterval(intervalo);
    };
  }, []);

  const atualizarDia = useCallback((indice, mudanca) => {
    setExecucao((anterior) => {
      if (!anterior) {
        return anterior;
      }
      const dias = anterior.dias.map((dia, posicao) =>
        posicao === indice ? { ...dia, ...mudanca } : dia,
      );
      return { ...anterior, dias };
    });
  }, []);

  const finalizar = useCallback((motivoFim, extras = {}) => {
    setExecucao((anterior) => {
      if (!anterior) {
        return anterior;
      }
      const dias = anterior.dias.map((dia) =>
        dia.status === "aguardando" || dia.status === "executando"
          ? { ...dia, status: "nao_executado", etapa: "" }
          : dia,
      );
      return { ...anterior, dias, estado: "finalizado", motivoFim, ...extras };
    });
  }, []);

  // Executa um dia. Devolve { criados } quando concluiu, ou { falha } com a
  // mensagem e se foi sessão expirada.
  const executarDia = useCallback(
    async (indice, data) => {
      atualizarDia(indice, { status: "executando", etapa: "Enviando pedido…" });

      let pedido;
      try {
        pedido = await comPrazo(
          caixaService.criarPedidoSisodonto(data, data),
          PRAZO_CRIACAO_PEDIDO_MS,
          "O servidor não respondeu ao pedido da integração.",
        );
      } catch (erro) {
        if (erro instanceof PrazoEsgotado) {
          // o pedido pode ter sido criado mesmo sem resposta: não repetir às cegas
          return {
            falha:
              "O servidor não respondeu ao pedido da integração. Confira a conexão e se há uma integração em andamento antes de tentar de novo.",
          };
        }
        if (erro.status === 503) {
          setAgente((anterior) => ({ ...anterior, online: false }));
          return { falha: MENSAGEM_ROBO_OFFLINE };
        }
        if (erro.status === 409) {
          return {
            falha:
              "Já existe uma integração em andamento (de outro administrador). Aguarde terminar e tente novamente.",
          };
        }
        return { falha: erro.message || "Erro ao solicitar a integração." };
      }

      const final = await aguardarPedidoFinal(pedido, {
        cancelado: () => !montadoRef.current,
        aoAtualizar: (atual) =>
          atualizarDia(indice, {
            etapa: ETAPAS[atual.status] || "Processando…",
          }),
      });

      if (final.status === "CONCLUIDO") {
        const resumo = final.resumo || {};
        return {
          criados: resumo.caixasCriados || 0,
          ignorados: resumo.datasIgnoradas?.length || 0,
        };
      }
      return {
        falha: mensagemDeFalhaDoPedido(final),
        sessaoExpirada: final.status === "SESSAO_EXPIRADA",
      };
    },
    [atualizarDia],
  );

  const iniciar = useCallback(
    async (dataInicial, dataFinal) => {
      // trava síncrona: dois cliques seguidos não iniciam duas execuções
      if (executandoRef.current) {
        return;
      }
      const datas = diasDoPeriodo(dataInicial, dataFinal);
      executandoRef.current = true;
      interrompidoRef.current = false;
      setExecucao({
        dias: datas.map((data) => ({
          data,
          status: "aguardando",
          etapa: "",
          mensagem: "",
        })),
        estado: "executando",
        interrompendo: false,
      });

      let totalCriados = 0;
      let indiceEmExecucao = -1;
      try {
        for (let indice = 0; indice < datas.length; indice += 1) {
          if (interrompidoRef.current) {
            finalizar("interrompido");
            return;
          }

          indiceEmExecucao = indice;
          const resultado = await executarDia(indice, datas[indice]);
          if (!montadoRef.current) {
            return;
          }

          if (resultado.falha) {
            atualizarDia(indice, {
              status: "erro",
              etapa: "",
              mensagem: resultado.falha,
            });
            finalizar("erro", {
              mensagemErro: resultado.falha,
              sessaoExpirada: Boolean(resultado.sessaoExpirada),
            });
            return;
          }

          totalCriados += resultado.criados;
          atualizarDia(
            indice,
            resultado.criados > 0
              ? { status: "criado", etapa: "", mensagem: "Caixa criado." }
              : {
                  status: "ignorado",
                  etapa: "",
                  mensagem: "Já tinha caixa lançado (ignorado).",
                },
          );
        }
        finalizar("sucesso");
      } catch (erro) {
        if (erro instanceof AcompanhamentoCancelado || !montadoRef.current) {
          return;
        }
        const mensagem =
          erro.message || "Não foi possível acompanhar a integração.";
        if (indiceEmExecucao >= 0) {
          atualizarDia(indiceEmExecucao, {
            status: "erro",
            etapa: "",
            mensagem,
          });
        }
        finalizar("erro", { mensagemErro: mensagem, sessaoExpirada: false });
      } finally {
        executandoRef.current = false;
        if (totalCriados > 0 && montadoRef.current) {
          retornosRef.current.onCaixasCriados?.(totalCriados);
        }
      }
    },
    [atualizarDia, executarDia, finalizar],
  );

  // Para depois do dia em andamento: um pedido já entregue ao robô não é cancelável.
  const interromper = useCallback(() => {
    interrompidoRef.current = true;
    setExecucao((anterior) =>
      anterior ? { ...anterior, interrompendo: true } : anterior,
    );
  }, []);

  // Volta ao passo de escolher o período (só depois de finalizar).
  const reiniciar = useCallback(() => {
    if (!executandoRef.current) {
      setExecucao(null);
    }
  }, []);

  return {
    agente,
    execucao,
    executando: execucao?.estado === "executando",
    iniciar,
    interromper,
    reiniciar,
  };
};

export default useIntegracaoSisodonto;
